import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { NgOptimizedImage } from "@angular/common";
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Product} from '../../../../../models/product';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ProductService} from '../../../../../services/product/product.service';
import {AdvertiserService} from '../../../../../services/advertiser/advertiser.service';
import {Advertiser} from '../../../../../models/advertiser';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {IsGranted} from '../../../../../decorator/is-granted';
import {AuthService} from '../../../../../services/auth/auth.service';
import {UploadFile} from '../../../../../models/upload-file';
import {UploadFileService} from '../../../../../services/upload-file/upload-file.service';
import {ImageService} from '../../../../../services/image/image.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {LangService} from '../../../../../services/language/lang.service';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {forkJoin} from 'rxjs';
import {Language} from '../../../../../models/language';

@Component({
  selector: 'app-product-form',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    InputSwitchComponent,
    NgOptimizedImage,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
],
  templateUrl: './product-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-form.component.scss'
})
@IsGranted(['ROLE_API_PRODUCT_WRITE', 'PRODUCT_VIEW'])
@MetaTitle('Product form')
export class ProductFormComponent implements OnInit {
  loaded = false;
  product: Product | null = null;
  advertisers: Advertiser[] = [];
  uploadImage: UploadFile | null = null;
  languages: Language[] = [];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    domain: new FormControl('', Validators.required),
    type: new FormControl('', Validators.required),
    enabled: new FormControl(true),
    identifier: new FormControl(''),
    cross: new FormControl(false),
    coreg: new FormControl(false),
    advertiserId: new FormControl<number | null>(null, Validators.required),
    languages: new FormControl<string[]>([]),
  });

  constructor(
    protected imageService: ImageService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private productService: ProductService,
    private advertiserService: AdvertiserService,
    private uploadFileService: UploadFileService,
    protected langService: LangService
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();
    forkJoin([
      this.advertiserService.findAll({sort: 'companyName'}),
      this.langService.getLanguages()
    ]).subscribe(([advertisers, languages]) => {
      this.advertisers = advertisers;
      this.languages = languages;

      const id = Number(this.route.snapshot.paramMap.get('id'));

      if (id) {
        this.productService.findOneById(id, {groups: 'advertiser'}).subscribe((product: Product) => {
          this.product = product;

          this.form.patchValue({
            name: this.product.name,
            domain: this.product.domain,
            type: this.product.type,
            enabled: this.product.enabled,
            identifier: this.product.identifier,
            advertiserId: this.product.advertiser?.id ?? null,
            coreg: this.product.coreg,
            cross: this.product.cross,
            languages: this.product.languages,
          });
          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.loaded = true;
        this.loader.stopLoading();
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    if (this.uploadImage) {
      params.logo = this.uploadImage;
    }

    if (this.isCreate()) {
      this.productService.create(params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Product has been created.');
        this.router.navigateByUrl('/configuration/products').then();
      });
    } else if (this.product?.id) {
      this.productService.update(this.product.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Product has been modified.');
        this.router.navigateByUrl('/configuration/products').then();
      });
    }
  }

  isCreate(): boolean {
    return !this.product;
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_PRODUCT_WRITE']);
  }

  onClickUpload(event: Event): void {
    const target: any = event.target;

    this.uploadDocument(target?.files[0]);
  }

  onDropUpload(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;

    if (files && files.length > 0) {
      this.uploadDocument(files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private uploadDocument(file: File): void {
    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.uploadImage = uploadFile;
    });
  }
}
